// Barcelona 2027 — refined editorial registration
import { useState, useMemo, useRef } from "react";
import { Check, ArrowRight, ArrowLeft, Shield, MapPin, Calendar, Users, Award, Info, Upload, X, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Refined palette
const NAVY = "#0E1B3D";
const NAVY_DEEP = "#080F26";
const GOLD = "#B8944D";
const CREAM = "#FAF7F2";
const INK = "#0E1B3D";

const HERO_IMAGE = "https://images.pexels.com/photos/34759875/pexels-photo-34759875.jpeg";

const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Argentina","Australia","Austria","Bangladesh","Belgium","Brazil","Cambodia",
  "Cameroon","Canada","Chile","China","Colombia","Denmark","Egypt","Ethiopia","Finland","France","Germany","Ghana",
  "Greece","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy","Japan","Jordan","Kenya","Lebanon","Malaysia",
  "Mexico","Morocco","Nepal","Netherlands","New Zealand","Nigeria","Norway","Pakistan","Palestine","Peru","Philippines",
  "Poland","Portugal","Qatar","Romania","Russia","Saudi Arabia","Singapore","South Africa","South Korea","Spain",
  "Sri Lanka","Sudan","Sweden","Switzerland","Syria","Taiwan","Tanzania","Thailand","Tunisia","Turkey","Uganda","Ukraine",
  "United Arab Emirates","United Kingdom","United States","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
];

const SEAT_OPTIONS = [
  {
    id: "self_funded",
    title: "Self-Funded",
    subtitle: "Guaranteed Seat",
    price: 850,
    priceNote: "USD · one-time",
    tag: "Instant Confirmation",
    highlights: [
      "Confirmed immediately upon payment",
      "No selection review — guaranteed seat",
      "Full program access & all inclusions",
    ],
  },
  {
    id: "fully_funded",
    title: "Fully-Funded",
    subtitle: "Merit Scholarship",
    price: 20,
    priceNote: "application fee only",
    tag: "10 seats · competitive",
    highlights: [
      "Full hotel accommodation (3 nights)",
      "All meals & program-related catering",
      "Access to all halls, workshops & sessions",
      "Local transport in Barcelona",
      "Visa invitation letter + guidance",
      "Note: international flights not included",
    ],
  },
];

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    full_name: "", country: "", email: "", whatsapp: "", age: "",
    organization: "", ambassador_code: "",
    seat_type: "",
    screening: {
      diplomacy_meaning: "", why_this_program: "",
      prior_experience: "", prior_experience_detail: "",
      country_choice: "", global_issue: "",
    },
    motivation: "",
    photo_path: "",
    media_consent: false,
    consent: false,
  });

  const selectedSeat = useMemo(() => SEAT_OPTIONS.find(s => s.id === form.seat_type), [form.seat_type]);
  const isFunded = form.seat_type === "fully_funded";

  const update = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => ({ ...e, [k]: undefined })); };
  const updateScreen = (k, v) => setForm(f => ({ ...f, screening: { ...f.screening, [k]: v } }));

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Image files only"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8 MB"); return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/uploads/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("photo_path", res.data.path);
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target.result);
      reader.readAsDataURL(file);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setPhotoUploading(false);
    }
  };
  const removePhoto = () => { update("photo_path", ""); setPhotoPreview(""); };

  const validate = () => {
    const e = {};
    if (step === 1) {
      if (!form.full_name.trim()) e.full_name = "Required";
      if (!form.country) e.country = "Required";
      if (!form.email.trim()) e.email = "Required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
      if (!form.whatsapp.trim() || form.whatsapp.trim().length < 4) e.whatsapp = "Required";
      if (!form.age || form.age < 16 || form.age > 35) e.age = "Age must be 16–35";
      if (!form.photo_path) e.photo_path = "Please upload a photo";
    } else if (step === 2) {
      const s = form.screening;
      if (!s.diplomacy_meaning.trim()) e.diplomacy_meaning = "Required";
      if (!s.why_this_program.trim()) e.why_this_program = "Required";
      if (!s.prior_experience) e.prior_experience = "Required";
      if (s.prior_experience === "yes" && !s.prior_experience_detail.trim()) e.prior_experience_detail = "Required";
      if (!s.country_choice.trim()) e.country_choice = "Required";
      if (!s.global_issue.trim()) e.global_issue = "Required";
      if (!form.motivation.trim() || form.motivation.trim().length < 50) e.motivation = "Motivation minimum 50 characters";
    } else if (step === 3) {
      if (!form.seat_type) e.seat_type = "Please select a seat type";
      if (!form.consent) e.consent = "You must confirm to continue";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const stepMap = [1, 2, 3];
  const stepLabels = ["Delegate", "Questions", "Seat & Payment"];
  const currentIdx = stepMap.indexOf(step);

  const nextStep = () => {
    if (!validate()) return;
    setStep(s => Math.min(s + 1, 3));
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prevStep = () => {
    setStep(s => Math.max(s - 1, 1));
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name, country: form.country, email: form.email,
        whatsapp: form.whatsapp, age: parseInt(form.age),
        organization: form.organization, ambassador_code: form.ambassador_code,
        seat_type: form.seat_type,
        screening: form.screening,
        motivation: form.motivation,
        photo_path: form.photo_path, media_consent: form.media_consent,
        consent: form.consent, origin_url: window.location.origin,
      };
      const res = await axios.post(`${API}/barcelona/register`, payload);
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: CREAM }} data-testid="register-page">
      {/* HERO — dramatic, editorial */}
      <section className="relative overflow-hidden" style={{ backgroundColor: NAVY_DEEP }}>
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Barcelona" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${NAVY_DEEP}FA 0%, ${NAVY}CC 60%, transparent 100%)` }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 py-24 lg:py-32 text-white">
          {/* Tiny badge */}
          <div className="inline-flex items-center gap-2 border border-white/20 backdrop-blur-sm px-4 py-1.5 mb-10">
            <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ backgroundColor: GOLD }} />
            <span className="text-[10px] tracking-[0.35em] uppercase text-white/80">Applications Open</span>
          </div>

          {/* Big serif hero */}
          <p className="text-[11px] tracking-[0.4em] uppercase mb-6" style={{ color: GOLD }}>
            GYP · Barcelona 2027
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] mb-8 max-w-4xl">
            International<br />
            <span style={{ color: GOLD, fontStyle: "italic", fontWeight: 300 }}>Leadership</span> &amp; Diplomacy
          </h1>
          <p className="text-white/70 text-lg md:text-xl leading-relaxed max-w-2xl mb-14 font-light">
            Fifty delegates. Twenty-plus countries. Three days of dialogue that will shape
            the next generation of diplomatic leadership — in one of Europe&apos;s great cities.
          </p>

          {/* Meta row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-10 border-t border-white/15 max-w-4xl">
            <MetaTile label="Program" value="Jan 15–18, 2027" />
            <MetaTile label="Venue" value="Barcelona · Spain" />
            <MetaTile label="Cohort" value="50 delegates" />
            <MetaTile label="Nations" value="20+ countries" />
          </div>
        </div>
      </section>

      {/* PROGRESS BAR */}
      <div className="border-b sticky top-0 z-20 backdrop-blur-md" style={{ backgroundColor: `${CREAM}EE`, borderColor: `${INK}12` }}>
        <div className="max-w-4xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>
                Step {currentIdx + 1} of {stepMap.length}
              </p>
              <span className="text-sm font-medium" style={{ color: INK }}>{stepLabels[currentIdx]}</span>
            </div>
            <div className="flex items-center gap-1.5">
              {stepMap.map((s, i) => (
                <div key={s} className="h-1 transition-all duration-300"
                  style={{
                    width: i === currentIdx ? "32px" : "12px",
                    backgroundColor: i <= currentIdx ? INK : `${INK}22`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* FORM */}
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16 lg:py-24">

        {step === 1 && (
          <FormSection eyebrow="Section 01" title="Delegate Information" subtitle="Tell us who you are. This information will appear on your delegate badge.">
            <div className="space-y-8">
              <FField label="Full Name" required error={errors.full_name}>
                <TextInput value={form.full_name} onChange={v => update("full_name", v)} placeholder="As it should appear on your badge" testid="input-full-name" />
              </FField>
              <div className="grid md:grid-cols-2 gap-8">
                <FField label="Country" required error={errors.country}>
                  <SelectInput value={form.country} onChange={v => update("country", v)} options={COUNTRIES} placeholder="Select" testid="select-country" />
                </FField>
                <FField label="Age" required error={errors.age}>
                  <TextInput type="number" min="16" max="35" value={form.age} onChange={v => update("age", v)} placeholder="16 – 35" testid="input-age" />
                </FField>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <FField label="Email" required error={errors.email}>
                  <TextInput type="email" value={form.email} onChange={v => update("email", v)} placeholder="you@example.com" testid="input-email" />
                </FField>
                <FField label="WhatsApp" required error={errors.whatsapp}>
                  <TextInput value={form.whatsapp} onChange={v => update("whatsapp", v)} placeholder="+1 234 567 890" testid="input-whatsapp" />
                </FField>
              </div>
              <FField label="University / Organization" hint="Optional">
                <TextInput value={form.organization} onChange={v => update("organization", v)} placeholder="Where you study or work" testid="input-org" />
              </FField>
              <FField label="Ambassador Referral Code" hint="Optional — enter code if referred by a GYP ambassador">
                <TextInput value={form.ambassador_code} onChange={v => update("ambassador_code", v)} placeholder="e.g. AMB-2027-042" testid="input-ambassador-code" />
              </FField>

              {/* Photo upload */}
              <div className="pt-8 border-t" style={{ borderColor: `${INK}12` }}>
                <FField label="Delegate Photo" required hint="Used on your badge and program materials. JPEG / PNG · Max 8 MB" error={errors.photo_path}>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => handlePhotoUpload(e.target.files?.[0])} data-testid="input-photo" />
                  {form.photo_path ? (
                    <div className="flex items-center gap-5 p-5 border" style={{ borderColor: `${INK}20`, backgroundColor: "white" }}>
                      {photoPreview && <img src={photoPreview} alt="preview" className="h-24 w-24 object-cover" />}
                      <div className="flex-1">
                        <p className="text-sm font-medium" style={{ color: INK }}>Photo uploaded</p>
                        <button type="button" onClick={removePhoto}
                          className="text-xs flex items-center gap-1 mt-1 hover:underline" style={{ color: "#B91C1C" }}
                          data-testid="remove-photo-btn">
                          <X className="h-3 w-3" /> Remove &amp; upload another
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={photoUploading}
                      className="w-full p-10 flex flex-col items-center gap-3 transition-colors disabled:opacity-60 border"
                      style={{ borderColor: `${INK}20`, backgroundColor: "white" }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = INK}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = `${INK}20`}
                      data-testid="upload-photo-btn">
                      {photoUploading ? (
                        <><Loader2 className="h-7 w-7 animate-spin" style={{ color: INK }} /><span className="text-xs tracking-[0.25em] uppercase" style={{ color: INK }}>Uploading...</span></>
                      ) : (
                        <>
                          <Upload className="h-7 w-7" style={{ color: INK }} strokeWidth={1.25} />
                          <span className="text-xs tracking-[0.25em] uppercase" style={{ color: INK }}>Upload Photo</span>
                          <span className="text-[10px] text-gray-500">JPEG / PNG · up to 8 MB</span>
                        </>
                      )}
                    </button>
                  )}
                </FField>
                <label className="flex items-start gap-3 cursor-pointer mt-5">
                  <input type="checkbox" checked={form.media_consent}
                    onChange={(e) => update("media_consent", e.target.checked)}
                    className="mt-1 h-4 w-4" style={{ accentColor: INK }}
                    data-testid="media-consent-checkbox" />
                  <span className="text-xs text-gray-600 leading-relaxed">
                    I consent to GYP using my photo on delegate badges, official conference materials,
                    and promotional media related to the Barcelona 2027 program.
                  </span>
                </label>
              </div>
            </div>
          </FormSection>
        )}

        {step === 2 && (
          <FormSection eyebrow="Section 02" title="Screening &amp; Motivation" subtitle="Tell us who you are and why you belong here. Reviewers read these carefully — be authentic and specific.">
            <div className="space-y-8">
              <FField label="In your own words, what does 'diplomacy' mean to you?" required error={errors.diplomacy_meaning}>
                <TextArea rows={3} value={form.screening.diplomacy_meaning} onChange={v => updateScreen("diplomacy_meaning", v)} testid="screen-diplomacy" />
              </FField>
              <FField label="Why this specific program, rather than any other youth conference?" required error={errors.why_this_program}>
                <TextArea rows={3} value={form.screening.why_this_program} onChange={v => updateScreen("why_this_program", v)} testid="screen-why" />
              </FField>
              <FField label="Have you attended a Model UN, youth summit, or similar international program before?" required error={errors.prior_experience}>
                <div className="flex gap-3">
                  {["yes","no"].map(v => (
                    <button key={v} type="button" onClick={() => updateScreen("prior_experience", v)}
                      className="px-8 py-3 text-xs uppercase tracking-[0.2em] transition-colors border"
                      style={{
                        borderColor: form.screening.prior_experience === v ? INK : `${INK}25`,
                        backgroundColor: form.screening.prior_experience === v ? INK : "white",
                        color: form.screening.prior_experience === v ? "white" : INK,
                      }}
                      data-testid={`screen-prior-${v}`}>
                      {v}
                    </button>
                  ))}
                </div>
              </FField>
              {form.screening.prior_experience === "yes" && (
                <FField label="Which one, and what was your role?" required error={errors.prior_experience_detail}>
                  <TextInput value={form.screening.prior_experience_detail} onChange={v => updateScreen("prior_experience_detail", v)} testid="screen-prior-detail" />
                </FField>
              )}
              <FField label="If you had to represent a country other than your own in a negotiation — which one, and why?" required error={errors.country_choice}>
                <TextArea rows={3} value={form.screening.country_choice} onChange={v => updateScreen("country_choice", v)} testid="screen-country" />
              </FField>
              <FField label="One global issue you'd want to discuss with someone from a completely different country?" required error={errors.global_issue}>
                <TextArea rows={3} value={form.screening.global_issue} onChange={v => updateScreen("global_issue", v)} testid="screen-issue" />
              </FField>
              <FField label="Motivation Statement" required hint={`${form.motivation.length} characters · minimum 50`} error={errors.motivation}>
                <TextArea rows={8} value={form.motivation} onChange={v => update("motivation", v)}
                  placeholder="Share your background, leadership experience, and what you hope to contribute and take away from Barcelona 2027..." testid="input-motivation" />
              </FField>
            </div>
          </FormSection>
        )}

        {step === 3 && (
          <FormSection eyebrow="Section 03" title="Select Seat &amp; Complete Payment" subtitle="Choose your seat type and complete your registration.">
            <div className="space-y-8">

              {/* Seat Selection */}
              <div className="space-y-5">
                {SEAT_OPTIONS.map(opt => {
                  const active = form.seat_type === opt.id;
                  return (
                    <button key={opt.id} type="button" onClick={() => update("seat_type", opt.id)}
                      className="w-full text-left transition-all group relative overflow-hidden"
                      style={{
                        backgroundColor: active ? INK : "white",
                        color: active ? "white" : INK,
                        border: `1px solid ${active ? INK : `${INK}22`}`,
                      }}
                      data-testid={`seat-option-${opt.id}`}>
                      <div className="p-8 md:p-10">
                        <div className="flex items-start justify-between gap-6">
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: GOLD }}>{opt.tag}</p>
                            <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-1">{opt.title}</h3>
                            <p className="text-sm mb-6" style={{ color: active ? "rgba(255,255,255,0.6)" : "#666" }}>{opt.subtitle}</p>
                            <ul className="space-y-2.5">
                              {opt.highlights.map(h => (
                                <li key={h} className="flex items-start gap-3 text-sm" style={{ color: active ? "rgba(255,255,255,0.85)" : "#333" }}>
                                  <span className="mt-2 h-1 w-3 shrink-0" style={{ backgroundColor: GOLD }} />
                                  <span>{h}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-serif text-5xl md:text-6xl leading-none">
                              <span style={{ color: GOLD }}>$</span>{opt.price}
                            </p>
                            <p className="text-[10px] tracking-[0.2em] uppercase mt-3" style={{ color: active ? "rgba(255,255,255,0.55)" : "#888" }}>{opt.priceNote}</p>
                            {active && (
                              <div className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase" style={{ color: GOLD }}>
                                <Check className="h-3 w-3" strokeWidth={3} /> Selected
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {active && <div className="absolute top-0 left-0 w-full h-0.5" style={{ backgroundColor: GOLD }} />}
                    </button>
                  );
                })}
              </div>
              {errors.seat_type && <p className="text-red-600 text-xs">{errors.seat_type}</p>}

              {/* Summary */}
              {selectedSeat && (
                <div className="p-8 border" style={{ borderColor: `${INK}18`, backgroundColor: "white" }}>
                  <p className="text-[10px] tracking-[0.3em] uppercase mb-6" style={{ color: GOLD }}>Application Summary</p>
                  <div className="grid md:grid-cols-2 gap-y-5 gap-x-8 text-sm">
                    <SummaryItem k="Name" v={form.full_name} />
                    <SummaryItem k="Country" v={form.country} />
                    <SummaryItem k="Email" v={form.email} />
                    <SummaryItem k="WhatsApp" v={form.whatsapp} />
                    <SummaryItem k="Age" v={form.age} />
                    {form.organization && <SummaryItem k="Organization" v={form.organization} />}
                  </div>
                  <div className="mt-8 pt-6 border-t flex items-end justify-between gap-6" style={{ borderColor: `${INK}12` }}>
                    <div>
                      <p className="text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-1">Selected Seat</p>
                      <p className="font-serif text-2xl" style={{ color: INK }}>{selectedSeat.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{selectedSeat.subtitle}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-serif text-4xl leading-none" style={{ color: INK }}>
                        <span style={{ color: GOLD }}>$</span>{selectedSeat.price}
                      </p>
                      <p className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mt-2">{selectedSeat.priceNote}</p>
                    </div>
                  </div>
                </div>
              )}

              <label className="flex items-start gap-4 cursor-pointer p-5 border" style={{ borderColor: form.consent ? INK : `${INK}20`, backgroundColor: "white" }} data-testid="consent-label">
                <input type="checkbox" checked={form.consent}
                  onChange={e => update("consent", e.target.checked)}
                  className="mt-0.5 h-5 w-5 shrink-0" style={{ accentColor: INK }}
                  data-testid="consent-checkbox" />
                <span className="text-sm text-gray-800 leading-relaxed">
                  I understand the payment is <strong style={{ color: INK }}>non-refundable</strong>,
                  and that funded seats are reviewed competitively — not guaranteed upon payment.
                </span>
              </label>
              {errors.consent && <p className="text-red-600 text-xs">{errors.consent}</p>}

              <div className="flex items-center gap-3 text-xs text-gray-500 justify-center">
                <Shield className="h-4 w-4" style={{ color: INK }} />
                Payment secured by <span className="font-medium" style={{ color: INK }}>Stripe</span> · SSL encrypted
              </div>
            </div>
          </FormSection>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-16 pt-8 border-t" style={{ borderColor: `${INK}15` }}>
          {step > 1 ? (
            <button onClick={prevStep} className="flex items-center gap-2 text-sm hover:opacity-70 transition-opacity" style={{ color: INK }} data-testid="prev-btn">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          ) : <div />}

          {step < 3 ? (
            <button onClick={nextStep}
              className="group px-10 py-4 text-xs tracking-[0.3em] uppercase flex items-center gap-3 transition-all hover:gap-4"
              style={{ backgroundColor: INK, color: "white" }}
              data-testid="next-btn">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting}
              className="group px-10 py-4 text-xs tracking-[0.3em] uppercase flex items-center gap-3 transition-all hover:gap-4 disabled:opacity-60"
              style={{ backgroundColor: GOLD, color: NAVY_DEEP }}
              data-testid="submit-btn">
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting...</>
              ) : (
                <><Award className="h-4 w-4" /> Pay ${selectedSeat?.price} &amp; Submit</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Small UI primitives ----------
const MetaTile = ({ label, value }) => (
  <div>
    <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2">{label}</p>
    <p className="font-serif text-lg md:text-xl">{value}</p>
  </div>
);

const FormSection = ({ eyebrow, title, subtitle, children }) => (
  <div>
    <div className="mb-10">
      <p className="text-[10px] tracking-[0.4em] uppercase mb-4" style={{ color: GOLD }}>{eyebrow}</p>
      <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-3" style={{ color: INK }}>{title}</h2>
      {subtitle && <p className="text-gray-600 max-w-xl leading-relaxed">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const FField = ({ label, required, hint, error, children }) => (
  <div>
    <div className="flex items-baseline justify-between mb-3">
      {label && (
        <label className="text-[11px] tracking-[0.25em] uppercase" style={{ color: INK }}>
          {label} {required && <span style={{ color: GOLD }}>*</span>}
        </label>
      )}
      {hint && !error && <span className="text-[10px] text-gray-400">{hint}</span>}
    </div>
    {children}
    {error && <p className="text-red-600 text-xs mt-2 tracking-wide">{error}</p>}
  </div>
);

const TextInput = ({ value, onChange, type = "text", placeholder, min, max, testid }) => (
  <input
    type={type} min={min} max={max} value={value} onChange={e => onChange(e.target.value)}
    placeholder={placeholder} data-testid={testid}
    className="w-full bg-transparent border-b py-3 text-base focus:outline-none transition-colors"
    style={{ borderColor: `${INK}30`, color: INK }}
    onFocus={(e) => e.target.style.borderColor = INK}
    onBlur={(e) => e.target.style.borderColor = `${INK}30`}
  />
);

const SelectInput = ({ value, onChange, options, placeholder, testid }) => (
  <select value={value} onChange={e => onChange(e.target.value)}
    data-testid={testid}
    className="w-full bg-transparent border-b py-3 text-base focus:outline-none appearance-none cursor-pointer"
    style={{ borderColor: `${INK}30`, color: INK, backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%230E1B3D' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 4px center" }}>
    <option value="">{placeholder}</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

const TextArea = ({ value, onChange, rows = 4, placeholder, testid }) => (
  <textarea
    rows={rows} value={value} onChange={e => onChange(e.target.value)}
    placeholder={placeholder} data-testid={testid}
    className="w-full bg-transparent border p-4 text-base focus:outline-none resize-none transition-colors"
    style={{ borderColor: `${INK}22`, color: INK, backgroundColor: "white" }}
    onFocus={(e) => e.target.style.borderColor = INK}
    onBlur={(e) => e.target.style.borderColor = `${INK}22`}
  />
);

const SummaryItem = ({ k, v }) => (
  <div>
    <p className="text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-1">{k}</p>
    <p style={{ color: INK }}>{v || "—"}</p>
  </div>
);
